import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { FormProvider, useForm } from 'react-hook-form'
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min'
import { Stack, Typography } from '@mui/material'
import { usePageHeader } from '../../../../utils/hooks'
import { useBlendingService } from 'services'
import { Button, Card, Comments } from '../../../../components'
import {
  BlendNameContainer,
  DetailsPageActionsButtons,
  DetailsPageActionsContainer,
  DetailsPageContainer,
  DetailsPageMainContent,
  DetailsPageSection,
} from '../../Blends.styled'
import { BlendParameters } from '../../components/BlendParameters/BlendPrameters'
import { SavedBlendRatio } from './components/SavedBlendRatio'
import { usePrint } from '../../../../components/features/Print'
import PrintModal from '../../../../components/features/Print/PrintModal'
import { SavedBlendDetailsHeaderInfo } from './components/SavedBlendDetailsHeaderInfo'

export const SavedBlendDetails = () => {
  const { blendId } = useParams()
  const { getBlendById, sendBlendPdf } = useBlendingService()

  const { data: blend } = useQuery({
    queryKey: ['blend_by_id', blendId],
    queryFn: () => getBlendById(blendId),
    select: (data) => data?.result,
  })

  // When passing ReactNode instead of string in headerTitle
  // it should be memoized to avoid loop
  const headerMemo = useMemo(
    () => ({
      headerTitle: <SavedBlendDetailsHeaderInfo blend={blend} />,
      showGoBack: true,
      goBackUrl: '/blends',
    }),
    [blend]
  )

  usePageHeader(headerMemo)

  const form = useForm()

  const getSelectedBins = () => {
    const selectedSuggestion = blend?.suggestions?.find((x) => x.selected)
    const selectedMixer = blend?.mixers?.find((x) => x.selected)

    return (
      selectedSuggestion?.suggestion_bins || selectedMixer?.mixer_bins || []
    )
  }

  const sendBlendPdfMutation = useMutation(
    (params: { blob: Blob; filename: string; blendId: number }) =>
      sendBlendPdf(params),
    {
      onSuccess: (data: any) => {
        toast.success(
          data?.detail ? data.detail : 'Email with PDF was sent successfully'
        )
      },
      onError: (error: any) => {
        let errorMessage = 'Failed to send PDF'
        if (error?.detail) {
          errorMessage = error.detail
        } else if (error?.message) {
          errorMessage = error.message
        } else if (typeof error === 'string') {
          errorMessage = error
        }
        toast.error(errorMessage)
      },
    }
  )

  const selectedBins = getSelectedBins()

  const { showPrintPreview, closePrintPreview, isPrintPreviewOpen } = usePrint()

  const BlendPrintContent = () => (
    <Stack gap={1}>
      <BlendNameContainer>
        <Typography fontSize="18" fontWeight="bold">
          Blend: {blend?.name || ''}
        </Typography>
        <Typography fontSize="18" fontWeight="bold">
          Product: {blend?.product?.name || ''}
        </Typography>
      </BlendNameContainer>
      <BlendParameters
        attributes={selectedAttributes}
        mode="disabled"
        displayAttributeValue
        displayHeader
      />
      <SavedBlendRatio selectedBins={selectedBins} />
    </Stack>
  )

  const handleSendPdf = () => {
    const element = document.getElementById('print-area')

    if (!element || !blend?.name) {
      return
    }

    const fileName = `Blend - ${blend.name}.pdf`
    const opt = {
      margin: 0.5,
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
    }

    html2pdf()
      .from(element)
      .set(opt)
      .outputPdf('blob')
      .then((pdfBlob) => {
        sendBlendPdfMutation.mutate({
          blob: pdfBlob,
          filename: fileName,
          blendId: blend.id,
        })
      })
  }

  const selectedAttributes =
    blend?.suggestions?.find((x) => x.selected)?.suggestion_attributes ||
    blend?.mixers?.find((x) => x.selected)?.mixer_attributes

  return (
    <FormProvider {...form}>
      <DetailsPageContainer>
        <DetailsPageMainContent>
          <DetailsPageSection>
            <BlendNameContainer>
              Product: {blend?.product?.name || ''}
            </BlendNameContainer>
            <BlendParameters
              attributes={selectedAttributes}
              mode="disabled"
              displayAttributeValue
              displayHeader
            />
            <SavedBlendRatio selectedBins={selectedBins} />
          </DetailsPageSection>
          <DetailsPageSection>
            <Card flex={1}>
              <Comments />
            </Card>
          </DetailsPageSection>
        </DetailsPageMainContent>
        <Card>
          <DetailsPageActionsContainer>
            <DetailsPageActionsButtons>
              <Button variantType="secondary" onClick={showPrintPreview}>
                Print
              </Button>
              <Button onClick={handleSendPdf}>Send</Button>
            </DetailsPageActionsButtons>
          </DetailsPageActionsContainer>
        </Card>
      </DetailsPageContainer>
      <PrintModal
        open={isPrintPreviewOpen}
        onClose={closePrintPreview}
        title={`Blend: ${blend?.name || 'Details'}`}
      >
        <BlendPrintContent />
      </PrintModal>
      <div style={{ display: 'none' }}>
        <div id="print-area">
          <BlendPrintContent />
        </div>
      </div>
    </FormProvider>
  )
}
